// src/context/ProductOrderStatsContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { productService } from '../services/productService';
import { useCustomerContext } from './CustomerContext';
import logger from '../utils/logger';

const ProductOrderStatsContext = createContext();

const MAX_CACHE_SIZE = 200;
const CACHE_CLEANUP_THRESHOLD = 250;

export const useProductOrderStatsContext = () => {
  const context = useContext(ProductOrderStatsContext);
  if (!context) {
    throw new Error('useProductOrderStatsContext must be used within a ProductOrderStatsProvider');
  }
  return context;
};

export const ProductOrderStatsProvider = ({ children }) => {
  const [orderStats, setOrderStats] = useState({});
  const [loading, setLoading] = useState(false);
  const fetchedProductsRef = useRef(new Set());
  const pendingRequestsRef = useRef(new Set());
  const accessTimeRef = useRef(new Map());
  const { selectedCustomer } = useCustomerContext();

  const cleanupOldEntries = useCallback(() => {
    const currentSize = fetchedProductsRef.current.size;

    if (currentSize <= MAX_CACHE_SIZE) return;

    const entriesToRemove = currentSize - MAX_CACHE_SIZE;
    const sortedByAccess = Array.from(accessTimeRef.current.entries())
      .sort((a, b) => a[1] - b[1]);

    const keysToRemove = sortedByAccess.slice(0, entriesToRemove).map(([key]) => key);

    setOrderStats(prev => {
      const newStats = { ...prev };
      keysToRemove.forEach(key => {
        delete newStats[key];
        fetchedProductsRef.current.delete(key);
        accessTimeRef.current.delete(key);
      });
      return newStats;
    });

    logger.log(`[ProductOrderStatsContext] LRU cleanup: removed ${keysToRemove.length} old entries`);
  }, []);

  useEffect(() => {
    setOrderStats({});
    fetchedProductsRef.current.clear();
    pendingRequestsRef.current.clear();
    accessTimeRef.current.clear();
    logger.log(`[ProductOrderStatsContext] Cache cleared due to customer change: ${selectedCustomer?.id || 'no-customer'}`);
  }, [selectedCustomer?.id]);

  // Fetch order stats for multiple products using bulk API
  const fetchOrderStatsForProducts = useCallback(async (productIds) => {
    if (!productIds || productIds.length === 0) return;

    const customerKey = selectedCustomer?.id || 'no-customer';

    // Filter out products that are already fetched or being fetched
    const unfetchedProductIds = productIds.filter(id => {
      const cacheKey = `${id}-${customerKey}`;
      return !fetchedProductsRef.current.has(cacheKey) && !pendingRequestsRef.current.has(cacheKey);
    });

    if (unfetchedProductIds.length === 0) return;

    logger.log(`[ProductOrderStatsContext] Fetching stats for ${unfetchedProductIds.length} products with customer context ${selectedCustomer?.id}`);

    // Mark products as being fetched
    unfetchedProductIds.forEach(id => {
      const cacheKey = `${id}-${customerKey}`;
      pendingRequestsRef.current.add(cacheKey);
    });

    try {
      setLoading(true);

      // Use bulk API for efficiency
      const result = await productService.getBulkProductOrderStats(unfetchedProductIds, selectedCustomer?.id);

      if (result.success) {
        logger.log(`[ProductOrderStatsContext] Bulk stats received for ${unfetchedProductIds.length} products:`, result.data);

        setOrderStats(prev => {
          const newStats = { ...prev };
          const now = Date.now();
          unfetchedProductIds.forEach(productId => {
            const cacheKey = `${productId}-${customerKey}`;
            newStats[cacheKey] = result.data[productId] || null;
            fetchedProductsRef.current.add(cacheKey);
            accessTimeRef.current.set(cacheKey, now);
          });
          return newStats;
        });

        if (fetchedProductsRef.current.size > CACHE_CLEANUP_THRESHOLD) {
          cleanupOldEntries();
        }
      } else {
        // Only log non-auth errors to avoid console spam
        if (!result.requiresAuth) {
          console.error(`[ProductOrderStatsContext] Failed to fetch bulk stats:`, result.error);
        }
      }
    } catch (error) {
      console.error(`[ProductOrderStatsContext] Error fetching bulk stats:`, error);
      unfetchedProductIds.forEach(id => {
        const cacheKey = `${id}-${customerKey}`;
        fetchedProductsRef.current.delete(cacheKey);
      });
    } finally {
      // Clear pending requests
      unfetchedProductIds.forEach(id => {
        const cacheKey = `${id}-${customerKey}`;
        pendingRequestsRef.current.delete(cacheKey);
      });
      setLoading(false);
    }
  }, [selectedCustomer?.id, cleanupOldEntries]);

  const getOrderStatsForProduct = useCallback((productId) => {
    const cacheKey = `${productId}-${selectedCustomer?.id || 'no-customer'}`;
    if (orderStats[cacheKey]) {
      accessTimeRef.current.set(cacheKey, Date.now());
    }
    return orderStats[cacheKey] || null;
  }, [orderStats, selectedCustomer?.id]);

  // Ensure products are fetched (called by view components)
  const ensureProductsFetched = useCallback((productIds) => {
    if (!productIds || productIds.length === 0) return;
    
    const customerKey = selectedCustomer?.id || 'no-customer';
    const unfetchedProductIds = productIds.filter(id => {
      const cacheKey = `${id}-${customerKey}`;
      return !fetchedProductsRef.current.has(cacheKey) && !pendingRequestsRef.current.has(cacheKey);
    });

    if (unfetchedProductIds.length > 0) {
      // Debounce the fetch to avoid multiple rapid calls
      const timeoutId = setTimeout(() => {
        fetchOrderStatsForProducts(unfetchedProductIds);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [fetchOrderStatsForProducts, selectedCustomer?.id]);

  useEffect(() => {
    const fetchedRef = fetchedProductsRef.current;
    const pendingRef = pendingRequestsRef.current;
    const accessRef = accessTimeRef.current;

    return () => {
      logger.log('[ProductOrderStatsContext] Unmounting - clearing cache');
      setOrderStats({});
      fetchedRef.clear();
      pendingRef.clear();
      accessRef.clear();
    };
  }, []);

  const value = {
    orderStats,
    loading,
    getOrderStatsForProduct,
    ensureProductsFetched,
    fetchOrderStatsForProducts
  };

  return (
    <ProductOrderStatsContext.Provider value={value}>
      {children}
    </ProductOrderStatsContext.Provider>
  );
};

export default ProductOrderStatsContext;